import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Checkbox } from "primereact/checkbox";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import useToast from "../../../../components/common/useToast";
import apiFunctions from "../../../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler, formatDateTime } from "../../../../utils/HelperFunctions";
import "../../../../assets/css/datatable.css";
import DeleteConfirmation from "../../../../components/modal-popup/delete-popup";
import WhatsAppIcon from "../../../../components/common/WhatsAppIcon";
import { Skeleton } from "primereact/skeleton";
import { ArrowLeftIcon, PlusIcon, CalendarDaysIcon, BellIcon } from "@heroicons/react/24/outline";

type EventItem = {
    _id: string;
    name: string;
    namegujarati: string;
    description: string;
    event_date: string;
    event_time: string;
    venue: string;
    venuegujarati: string;
    isactive: boolean;
    notified_at: string | null;
    createddate: string;
};

type PickerUser = {
    id: string;
    name: string;
    surname: string;
    mobileno: string;
};

const EventPage: React.FC = () => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [eventData, setEventData] = useState<EventItem[]>([]);
    const [dataLoad, setDataLoad] = useState(true);
    const [load, setLoad] = useState(true);
    const initialized = useRef(false);
    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState<any>(0);
    const { showToast }: any = useToast();
    const [name, setName] = useState<string>("");

    const [eventToDelete, setEventToDelete] = useState<EventItem | null>(null);
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);

    /* notify options — used by the per-row Notify dialog */
    const [notifyPush, setNotifyPush] = useState(false);
    const [notifyWhatsApp, setNotifyWhatsApp] = useState(false);
    const [notifyAllUsers, setNotifyAllUsers] = useState(true);
    const [selectedUsers, setSelectedUsers] = useState<PickerUser[]>([]);

    /* user picker */
    const PICKER_PAGE_SIZE = 50;
    const [pickerUsers, setPickerUsers] = useState<PickerUser[]>([]);
    const [pickerSearch, setPickerSearch] = useState("");
    const [pickerLoading, setPickerLoading] = useState(false);
    const [pickerLoadingMore, setPickerLoadingMore] = useState(false);
    const [pickerPage, setPickerPage] = useState(0);
    const [pickerTotal, setPickerTotal] = useState(0);

    /* per-row notify dialog */
    const [notifyDialogEvent, setNotifyDialogEvent] = useState<EventItem | null>(null);
    const [notifying, setNotifying] = useState(false);

    const startIndex = (currentPage - 1) * itemsPerPage;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    async function getEventData() {
        try {
            setDataLoad(true);
            const params: any = {
                page: currentPage + 1,
                limit: itemsPerPage,
            };
            if (name) params.search = name;

            const response = await apiFunctions.get("/event", { params });

            if (response?.data?.success) {
                setEventData(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response?.data?.message || "Failed to load events",
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setDataLoad(false);
            setLoad(false);
        }
    }

    const getsearchdata = (e: any) => {
        if (e.target.value.length >= 2) {
            setName(e.target.value);
            setCurrentPage(0);
        } else {
            setName("");
        }
    };

    useEffect(() => {
        if (!load) {
            getEventData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, name]);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getEventData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* load a page of users for the picker (first page resets the list, later pages append) */
    async function loadPickerUsers(search: string, page: number) {
        try {
            if (page === 0) setPickerLoading(true);
            else setPickerLoadingMore(true);

            const url = search.trim().length > 1
                ? `/user/list/${page * PICKER_PAGE_SIZE}/${PICKER_PAGE_SIZE}/createddate/desc/${encodeURIComponent(search.trim())}`
                : `/user/list/${page * PICKER_PAGE_SIZE}/${PICKER_PAGE_SIZE}/createddate/desc`;

            const response = await apiFunctions.get(url);
            if (response?.data?.success) {
                setPickerUsers((prev) => (page === 0 ? response.data.data || [] : [...prev, ...(response.data.data || [])]));
                setPickerTotal(response.data.totalRecords || 0);
                setPickerPage(page);
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setPickerLoading(false);
            setPickerLoadingMore(false);
        }
    }

    /* fetch picker list (page 0) when "selected users" mode opens, and again on search */
    useEffect(() => {
        if (notifyDialogEvent && (notifyPush || notifyWhatsApp) && !notifyAllUsers) {
            loadPickerUsers(pickerSearch, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifyAllUsers, pickerSearch, notifyPush, notifyWhatsApp]);

    // load the next page once the user scrolls near the bottom of the picker list
    const handlePickerScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
        if (nearBottom && !pickerLoading && !pickerLoadingMore && pickerUsers.length < pickerTotal) {
            loadPickerUsers(pickerSearch, pickerPage + 1);
        }
    };

    const resetNotifyOptions = () => {
        setNotifyPush(false);
        setNotifyWhatsApp(false);
        setNotifyAllUsers(true);
        setSelectedUsers([]);
        setPickerSearch("");
        setPickerUsers([]);
        setPickerPage(0);
        setPickerTotal(0);
    };

    const handleBack = () => {
        navigate('/admin/settings');
    };

    /* validates the notify block; returns payload fields or null on error */
    const buildNotifyPayload = () => {
        if (!notifyAllUsers && selectedUsers.length === 0) {
            showToast({ severity: "warn", summary: "Warning", detail: "Please select at least one user", life: 3000 });
            return null;
        }
        return {
            notify_push: notifyPush,
            notify_whatsapp: notifyWhatsApp,
            target_type: notifyAllUsers ? "all_users" : "selected_users",
            userIds: notifyAllUsers ? [] : selectedUsers.map((u) => u.id),
        };
    };

    const handleToggleActive = async (rowData: EventItem, value: boolean) => {
        try {
            await apiFunctions.patch(`/event/${rowData._id}`, { isactive: value });
            getEventData();
        } catch (err) {
            errorHandler(err as AxiosError);
        }
    };

    const openNotifyDialog = (rowData: EventItem) => {
        resetNotifyOptions();
        setNotifyDialogEvent(rowData);
    };

    const handleNotifySend = async () => {
        if (!notifyDialogEvent) return;

        if (!notifyPush && !notifyWhatsApp) {
            showToast({ severity: "warn", summary: "Warning", detail: "Select at least one channel", life: 3000 });
            return;
        }

        const notifyPayload = buildNotifyPayload();
        if (!notifyPayload) return;

        try {
            setNotifying(true);
            const response = await apiFunctions.post(`/event/${notifyDialogEvent._id}/notify`, notifyPayload);
            if (response?.data?.success) {
                showToast({
                    severity: "info",
                    summary: "Notifications Started",
                    detail: response.data.message,
                    life: 4000,
                });
                setNotifyDialogEvent(null);
                resetNotifyOptions();
                getEventData();
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setNotifying(false);
        }
    };

    const handleDelete = (rowData: EventItem) => {
        setEventToDelete(rowData);
        setDeleteConfirmationVisible(true);
    };

    const hideDeleteConfirmation = () => {
        setDeleteConfirmationVisible(false);
        setEventToDelete(null);
    };

    const confirmDelete = async () => {
        if (eventToDelete) {
            try {
                const response = await apiFunctions.delete(`/event/${eventToDelete._id}`);
                if (response?.data?.success) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Event deleted successfully", life: 3000 });
                } else {
                    showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Delete failed", life: 3000 });
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setDeleteConfirmationVisible(false);
                setEventToDelete(null);
                getEventData();
            }
        }
    };

    const eventDateBody = (rowData: EventItem) => {
        if (!rowData.event_date) return "-";
        const d = new Date(rowData.event_date);
        const dateStr = d.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
        return rowData.event_time ? `${dateStr} ${rowData.event_time}` : dateStr;
    };

    const actionBodyTemplate = (rowData: EventItem) => {
        return (
            <div className="action-icons flex items-center gap-2 flex-wrap">
                <Button
                    outlined
                    size="small"
                    style={{ color: "#16a34a", borderColor: "#16a34a" }}
                    tooltip="Send notification (push / WhatsApp)"
                    tooltipOptions={{ position: "top" }}
                    onClick={() => openNotifyDialog(rowData)}
                >
                    <span className="flex items-center gap-1">
                        <BellIcon style={{ width: 14, height: 14 }} />
                        Notify
                    </span>
                </Button>
                <Button label="Edit" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => navigate(`/admin/event/edit/${rowData._id}`)} />
                <Button label="Delete" outlined size="small" style={{ color: "#dc2626", borderColor: "#dc2626" }} onClick={() => handleDelete(rowData)} />
            </div>
        );
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
            ];

            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries-text mx-1">entries</span>;
        },
    };

    /* notify options UI — used inside the per-row Notify dialog */
    const notifySection = (
        <div className="flex flex-col gap-2 border rounded p-3" style={{ borderColor: "#d1d5db" }}>
            <label className="text-sm font-semibold">Notifications</label>

            <div className="flex items-center gap-2">
                <input
                    type="checkbox"
                    id="notify-push"
                    checked={notifyPush}
                    onChange={(e) => setNotifyPush(e.target.checked)}
                />
                <label htmlFor="notify-push" className="text-sm flex items-center gap-1">
                    <BellIcon style={{ width: 15, height: 15 }} />
                    Send Firebase push notification
                </label>
            </div>

            <div className="flex items-center gap-2">
                <input
                    type="checkbox"
                    id="notify-whatsapp"
                    checked={notifyWhatsApp}
                    onChange={(e) => setNotifyWhatsApp(e.target.checked)}
                />
                <label htmlFor="notify-whatsapp" className="text-sm flex items-center gap-1">
                    <span style={{ color: "#25D366", display: "inline-flex" }}><WhatsAppIcon size={14} /></span>
                    Send WhatsApp message (Gujarati template)
                </label>
            </div>

            {(notifyPush || notifyWhatsApp) && (
                <>
                    <div className="flex items-center gap-2 mt-1">
                        <input
                            type="checkbox"
                            id="notify-all"
                            checked={notifyAllUsers}
                            onChange={(e) => setNotifyAllUsers(e.target.checked)}
                        />
                        <label htmlFor="notify-all" className="text-sm">Send to all users</label>
                    </div>

                    {!notifyAllUsers && (
                        <div className="flex flex-col gap-2 border rounded p-2" style={{ borderColor: "#e5e7eb" }}>
                            <div className="flex items-center justify-between gap-2">
                                <PrimeInputText
                                    type="text"
                                    value={pickerSearch}
                                    onChange={(e) => setPickerSearch(e.target.value)}
                                    placeholder="Search users..."
                                    className="w-full"
                                    style={{ padding: "6px 10px" }}
                                />
                                <span className="text-xs whitespace-nowrap font-medium" style={{ color: "#16a34a" }}>
                                    {selectedUsers.length} selected
                                </span>
                            </div>

                            <div
                                style={{ maxHeight: "180px", overflowY: "auto" }}
                                className="flex flex-col gap-1"
                                onScroll={handlePickerScroll}
                            >
                                {pickerLoading ? (
                                    <span className="text-xs text-center py-3" style={{ color: "#9ca3af" }}>Loading users...</span>
                                ) : pickerUsers.length === 0 ? (
                                    <span className="text-xs text-center py-3" style={{ color: "#9ca3af" }}>No users found</span>
                                ) : (
                                    pickerUsers.map((u: any) => {
                                        const isSelected = selectedUsers.some((s) => s.id === u.id);
                                        return (
                                            <div key={u.id} className="flex items-center gap-2 px-1 py-1 rounded" style={{ background: isSelected ? "#f0fdf4" : "transparent" }}>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        if (e.checked) {
                                                            setSelectedUsers((prev) => [...prev, u]);
                                                        } else {
                                                            setSelectedUsers((prev) => prev.filter((s) => s.id !== u.id));
                                                        }
                                                    }}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <span className="text-sm">{u.name} {u.surname}</span>
                                                    <span className="text-xs ml-2" style={{ color: "#9ca3af" }}>{u.mobileno || ""}</span>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                {pickerLoadingMore && (
                                    <span className="text-xs text-center py-2" style={{ color: "#9ca3af" }}>Loading more...</span>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    const header = (
        <div className="grid grid-cols-12 gap-4 items-center table-header">
            <div className="col-span-12 sm:col-span-6">
                <h2 className="card-title m-0 text-lg sm:text-xl">Events</h2>
            </div>
            <div className="col-span-12 sm:col-span-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-gray-600">Search:</span>
                    <PrimeInputText
                        type="text"
                        className="search-input w-full sm:w-auto"
                        placeholder="Search by name or venue..."
                        onChange={(e) => getsearchdata(e)}
                    />
                </div>
                <div className="round-btn-div">
                    <Button
                        icon={<PlusIcon className="w-6 h-6" />}
                        className="round-button"
                        onClick={() => navigate("/admin/event/create")}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className="p-4 standalone-page-bg">
            <div className="flex items-center gap-2 mb-2 cursor-pointer w-max" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" />
                <h3 className="m-0 text-lg sm:text-xl">Back</h3>
            </div>
            <div className="table-margin inner-table-margin">
                {isMobile ? (
                    <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                        <div className="p-3 pb-2">{header}</div>
                        <div className="flex flex-col gap-2 px-3 pb-3">
                            {dataLoad ? (
                                Array.from({ length: itemsPerPage }, (_, i) => (
                                    <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <Skeleton width="60%" height="1rem" className="mb-2" />
                                        <Skeleton width="40%" height="0.8rem" />
                                    </div>
                                ))
                            ) : eventData.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "64px 0" }}>
                                    <CalendarDaysIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                    <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No events found</p>
                                </div>
                            ) : (
                                eventData.map((rowData) => (
                                    <div key={rowData._id} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <p className="m-0 font-semibold text-sm break-words" style={{ color: "#264164" }}>
                                            {rowData.name}{rowData.namegujarati ? ` (${rowData.namegujarati})` : ""}
                                        </p>
                                        <p className="m-0 text-xs break-words mt-1" style={{ color: "#4b5563" }}>
                                            {eventDateBody(rowData)}{rowData.venue ? ` · ${rowData.venue}` : ""}
                                        </p>
                                        {rowData.notified_at && (
                                            <p className="m-0 text-xs mt-1" style={{ color: "#16a34a" }}>
                                                Notified: {formatDateTime(rowData.notified_at)}
                                            </p>
                                        )}
                                        <div className="flex items-center gap-2 mt-2">
                                            {/* shrink-0: without it, a flex row can squeeze the
                                                switch's own width down to fit its neighboring text
                                                on a narrow screen instead of wrapping the text,
                                                rendering the slider/knob squashed. */}
                                            <InputSwitch checked={rowData.isactive} onChange={(e) => handleToggleActive(rowData, e.value)} className="shrink-0" />
                                            <span className="text-xs" style={{ color: "#4b5563" }}>{rowData.isactive ? "Active" : "Inactive"}</span>
                                        </div>
                                        <div className="flex justify-end gap-2 mt-2 flex-wrap">
                                            {actionBodyTemplate(rowData)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ) : (
                    <DataTable
                        scrollable
                        scrollHeight="calc(100vh - 215px)"
                        style={{ minHeight: "calc(100vh - 140px)", background: "#fff" }}
                        header={header}
                        value={
                            dataLoad
                                ? Array.from({ length: itemsPerPage }, (_, i) => ({ _id: `_skeleton_${i}` }))
                                : eventData
                        }
                        resizableColumns
                        rows={itemsPerPage}
                        emptyMessage={
                            <div style={{ textAlign: "center", padding: "64px 0" }}>
                                <CalendarDaysIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No events found</p>
                            </div>
                        }
                    >
                        <Column
                            field="name"
                            header="Event Name"
                            style={{ minWidth: "200px" }}
                            body={(rowData) => dataLoad ? <Skeleton /> : (
                                <div>
                                    <div>{rowData.name}</div>
                                    {rowData.namegujarati && <div className="text-xs" style={{ color: "#6b7280" }}>{rowData.namegujarati}</div>}
                                </div>
                            )}
                        />
                        <Column header="Date & Time" style={{ minWidth: "150px" }} body={(rowData) => dataLoad ? <Skeleton /> : eventDateBody(rowData)} />
                        <Column
                            field="venue"
                            header="Venue"
                            style={{ minWidth: "160px" }}
                            body={(rowData) => dataLoad ? <Skeleton /> : (
                                <div>
                                    <div>{rowData.venue || "-"}</div>
                                    {rowData.venuegujarati && <div className="text-xs" style={{ color: "#6b7280" }}>{rowData.venuegujarati}</div>}
                                </div>
                            )}
                        />
                        <Column
                            header="Notified"
                            style={{ minWidth: "150px" }}
                            body={(rowData) => dataLoad ? <Skeleton /> : (
                                rowData.notified_at
                                    ? <span style={{ color: "#16a34a" }}>{formatDateTime(rowData.notified_at)}</span>
                                    : <span style={{ color: "#9ca3af" }}>-</span>
                            )}
                        />
                        <Column
                            header="Status"
                            style={{ minWidth: "100px" }}
                            body={(rowData) => dataLoad ? <Skeleton /> : (
                                <InputSwitch checked={rowData.isactive} onChange={(e) => handleToggleActive(rowData, e.value)} className="shrink-0" />
                            )}
                        />
                        <Column
                            field="createddate"
                            header="Created Date"
                            style={{ minWidth: "170px" }}
                            body={(rowData) => dataLoad ? <Skeleton /> : formatDateTime(rowData.createddate)}
                        />
                        <Column header="Action" className="action-column" frozen alignFrozen="right" style={{ minWidth: "280px" }} body={(rowData) => dataLoad ? <Skeleton /> : actionBodyTemplate(rowData)} />
                    </DataTable>
                )}
                {totalRecords > 0 && <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                    <Paginator
                        template={paginatoreTemplate}
                        first={startIndex}
                        rows={itemsPerPage}
                        totalRecords={totalRecords}
                    />
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage === 0}
                        >
                            <span className="sm:hidden">Prev</span>
                            <span className="hidden sm:inline">Previous</span>
                        </Button>

                        <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                            <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                Page {currentPage + 1} of {totalPages || 1}
                            </span>
                        </Button>

                        <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                        >
                            Next
                        </Button>
                    </div>
                </div>}
            </div>

            {/* Per-row Notify dialog */}
            <Dialog
                header={`Notify Users — ${notifyDialogEvent?.name || ""}`}
                visible={!!notifyDialogEvent}
                style={{ width: "90vw", maxWidth: "480px" }}
                modal
                draggable={false}
                onHide={() => { setNotifyDialogEvent(null); resetNotifyOptions(); }}
            >
                <div className="flex flex-col gap-3">
                    {notifyDialogEvent?.notified_at && (
                        <div className="text-xs px-3 py-2 rounded" style={{ background: "#fefce8", color: "#a16207" }}>
                            Already notified on {formatDateTime(notifyDialogEvent.notified_at)}
                        </div>
                    )}

                    {notifySection}

                    <div className="flex justify-end gap-2 mt-2">
                        <Button
                            label="Cancel"
                            severity="secondary"
                            onClick={() => { setNotifyDialogEvent(null); resetNotifyOptions(); }}
                            disabled={notifying}
                        />
                        <Button
                            label="Send"
                            severity="success"
                            loading={notifying}
                            disabled={!notifyPush && !notifyWhatsApp}
                            onClick={handleNotifySend}
                        />
                    </div>
                </div>
            </Dialog>

            <DeleteConfirmation
                visible={deleteConfirmationVisible}
                onHide={hideDeleteConfirmation}
                onDelete={confirmDelete}
                header="Delete Event"
                message={`Are you sure you want to delete "${eventToDelete?.name}"? This action cannot be undone.`}
            />
        </div>
    );
};

export default EventPage;
